import { supabase } from './supabase';
import { rowToPhoto, type Photo, type PhotoRow, type Comment } from '@/types';
import { getDeviceId } from './device';
import { telegramFileUrl } from './telegram';
import { sendPhotoDirectToTelegram } from './telegram-direct';

const MY_PHOTOS_KEY = 'guestlens_my_photo_ids';

export function getMyPhotoIds(): string[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(MY_PHOTOS_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

export function addMyPhotoId(id: string): void {
  if (typeof window === 'undefined' || !id) return;
  try {
    const ids = getMyPhotoIds();
    if (!ids.includes(id)) {
      ids.push(id);
      localStorage.setItem(MY_PHOTOS_KEY, JSON.stringify(ids));
    }
  } catch {}
}

/**
 * Actualizează `uploaded_by` pe pozele încărcate de acest device/utilizator
 * când își schimbă numele. Fail-silent.
 */
export async function syncUploaderName(newName: string, oldName?: string): Promise<void> {
  if (!newName) return;
  try {
    const myIds = getMyPhotoIds();
    if (myIds.length > 0) {
      const { error: e1 } = await supabase
        .from('photos')
        .update({ uploaded_by: newName })
        .in('id', myIds);
      if (e1) console.warn('[syncUploaderName] id update failed:', e1.message);
    }
    if (oldName && oldName.trim() && oldName.trim() !== newName.trim()) {
      const { error: e2 } = await supabase
        .from('photos')
        .update({ uploaded_by: newName })
        .eq('uploaded_by', oldName.trim());
      if (e2) console.warn('[syncUploaderName] name match update failed:', e2.message);
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[syncUploaderName] exception:', e);
  }
}

/** Listează toate pozele aprobate (cele vizibile public). */
export async function listApprovedPhotos(): Promise<Photo[]> {
  const { data, error } = await supabase
    .from('photos')
    .select('*')
    .eq('status', 'approved')
    .order('created_at', { ascending: false });
  if (error) throw error;
  if (!data || data.length === 0) return [];

  const ids = data.map((p) => p.id);
  const [{ data: likeRows }, { data: commentRows }, { data: viewRows }] = await Promise.all([
    supabase.from('likes').select('photo_id').in('photo_id', ids),
    supabase.from('comments').select('photo_id').in('photo_id', ids),
    supabase.from('views').select('photo_id').in('photo_id', ids),
  ]);

  const likeCounts = new Map<string, number>();
  (likeRows ?? []).forEach((r: any) => {
    likeCounts.set(r.photo_id, (likeCounts.get(r.photo_id) ?? 0) + 1);
  });
  const commentCounts = new Map<string, number>();
  (commentRows ?? []).forEach((r: any) => {
    commentCounts.set(r.photo_id, (commentCounts.get(r.photo_id) ?? 0) + 1);
  });
  const viewCounts = new Map<string, number>();
  (viewRows ?? []).forEach((r: any) => {
    viewCounts.set(r.photo_id, (viewCounts.get(r.photo_id) ?? 0) + 1);
  });

  return (data as PhotoRow[]).map((row) =>
    rowToPhoto(row, {
      likes: likeCounts.get(row.id) ?? 0,
      comments: commentCounts.get(row.id) ?? 0,
      views: viewCounts.get(row.id) ?? (row.views_count || 0),
    })
  );
}

/** Comentarii pentru o poză. */
export async function listComments(photoId: string): Promise<Comment[]> {
  const { data, error } = await supabase
    .from('comments')
    .select('*')
    .eq('photo_id', photoId)
    .order('created_at', { ascending: true });
  if (error) throw error;
  return (data ?? []) as Comment[];
}

/** Verifică dacă userul curent a dat like. */
export async function hasLiked(photoId: string): Promise<boolean> {
  const token = getDeviceId();
  if (!token) return false;
  const { data, error } = await supabase
    .from('likes')
    .select('photo_id')
    .eq('photo_id', photoId)
    .eq('user_token', token)
    .maybeSingle();
  if (error) throw error;
  return !!data;
}

/** Toggle like pentru o poză. */
export async function toggleLike(photoId: string): Promise<{ liked: boolean; count: number }> {
  const token = getDeviceId();
  if (!token) throw new Error('No device id');

  // Check current
  const liked = await hasLiked(photoId);
  if (liked) {
    const { error } = await supabase
      .from('likes')
      .delete()
      .eq('photo_id', photoId)
      .eq('user_token', token);
    if (error) throw error;
  } else {
    const { error } = await supabase
      .from('likes')
      .insert({ photo_id: photoId, user_token: token });
    if (error) throw error;
  }

  const { count, error: e2 } = await supabase
    .from('likes')
    .select('*', { count: 'exact', head: true })
    .eq('photo_id', photoId);
  if (e2) throw e2;
  return { liked: !liked, count: count ?? 0 };
}

/** Adaugă comentariu. device_id este trimis ca să putem bloca userii banati. */
export async function addComment(photoId: string, author: string, body: string, deviceId?: string): Promise<Comment> {
  const token = deviceId ?? getDeviceId();
  const { data, error } = await supabase
    .from('comments')
    .insert({ photo_id: photoId, author, body, device_id: token ?? null })
    .select()
    .single();
  if (error) throw error;
  return data as Comment;
}

/** Marchez o vizualizare (uniqueness enforced by unique(photo_id, user_token)). */
export async function markView(photoId: string): Promise<void> {
  const token = getDeviceId();
  if (!token) return;
  // upsert no-op: if already viewed, do nothing
  const { error } = await supabase
    .from('views')
    .insert({ photo_id: photoId, user_token: token });
  // ignore unique-violation errors silently
  if (error && !String(error.message).includes('duplicate')) {
    // eslint-disable-next-line no-console
    console.warn('[markView]', error.message);
  }
}

/**
 * Upload photo flow (fără Worker):
 * 1. Trimite direct la Telegram cu VITE_B_TOKEN
 * 2. Daca reuseste, INSERT pending row in Supabase cu file_id
 *
 * Daca Telegram esueaza, nu se salveaza nimic in DB (nimic orfan).
 *
 * IMPORTANT: uploaded_by = display_name (pentru citiri existente in UI),
 * uploader_uuid = device_id real (folosit de trigger pt ban).
 */
export async function uploadPhoto(opts: {
  uploadedBy: string;
  visitorId?: string;
  caption?: string;
  file: Blob;
}): Promise<{ id: string; status: 'pending' | 'approved' | 'rejected' }> {
  // 1. Trimite direct la Telegram
  const caption = `📸 ${opts.uploadedBy}${opts.caption ? '\n' + opts.caption : ''}`;
  let out: { file_id: string; message_id: number; channel: string };
  try {
    out = await sendPhotoDirectToTelegram(opts.file, caption);
  } catch (err) {
    throw new Error(`Telegram upload failed: ${err instanceof Error ? err.message : String(err)}`);
  }

  // 2. INSERT pending row cu file_id
  const { data: row, error: e1 } = await supabase
    .from('photos')
    .insert({
      uploaded_by: opts.uploadedBy,
      uploader_uuid: opts.visitorId ?? null,
      caption: opts.caption ?? null,
      status: 'pending',
      telegram_file_id: out.file_id,
      telegram_message_id: out.message_id,
      telegram_channel: out.channel,
    })
    .select()
    .single();
  if (e1) {
    console.error('[uploadPhoto] Telegram OK but DB insert failed:', e1, {
      file_id: out.file_id,
      message_id: out.message_id,
    });
    // Aruncam o Error reala cu mesaj lizibil, nu PostgrestError plain
    const msg = e1.message || (e1.hint as string) || JSON.stringify(e1);
    throw new Error(`DB insert failed: ${msg}`);
  }

  // 3. Salvează ID-ul local pentru a putea sincroniza numele dacă se schimbă ulterior
  if (row?.id) {
    addMyPhotoId(row.id);
  }

  return { id: row.id, status: 'pending' };
}

/** Helper: URL pentru thumbnail photo. */
export function photoThumbnail(photo: Photo): string {
  if (!photo.telegram_file_id) return '';
  return telegramFileUrl(photo.telegram_file_id);
}
